"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropV2 } from "@/generated/models/ApiDropV2";
import type { ApiDropV2PageWithoutCount } from "@/generated/models/ApiDropV2PageWithoutCount";
import { DropSize, type ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { commonApiFetch } from "@/services/api/common-api";
import { createFallbackWaveMin } from "@/services/api/drop-v2-mappers";
import { mapLeaderboardDropV2 } from "@/services/api/wave-drops-v2-api";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
export const COMMUNITY_CURATIONS_DROPS_QUERY_KEY = "COMMUNITY_CURATIONS_DROPS";
interface UseCommunityCurationsDropsProps {
  readonly limit: number;
  readonly enabled?: boolean | undefined;
}
interface CommunityCurationsDropsPage {
  readonly data: ApiDrop[];
  readonly page: number;
  readonly next: boolean;
}
const getUniqueDrops = (pages: CommunityCurationsDropsPage[] | undefined): ExtendedDrop[] => {
  if (!pages) {
    return [];
  }
  const seen = new Set<string>();
  const drops: ExtendedDrop[] = [];
  for (const page of pages) {
    for (const drop of page.data) {
      if (seen.has(drop.id)) {
        continue;
      }
      seen.add(drop.id);
      drops.push({
        ...drop,
        type: DropSize.FULL,
        stableKey: drop.id,
        stableHash: drop.id
      });
    }
  }
  return drops;
};
const mapCommunityCurationDropV2 = (drop: ApiDropV2): ApiDrop => {
  const wave = createFallbackWaveMin(drop.author.badges.profile_wave_id ?? "");
  const mappedDrop = mapLeaderboardDropV2({
    drop,
    wave
  });
  return {
    ...mappedDrop,
    wave
  };
};
const fetchCommunityCurationsDrops = ({
  limit,
  page
}: {
  readonly limit: number;
  readonly page: number;
}): Promise<CommunityCurationsDropsPage> => commonApiFetch<ApiDropV2PageWithoutCount>({
  endpoint: "v2/curated-profile-wave-drops",
  params: {
    page: `${page}`,
    page_size: `${limit}`
  }
}).then(response => ({
  data: response.data.map(mapCommunityCurationDropV2),
  page: response.page,
  next: response.next
}));
export function useCommunityCurationsDrops(t0) {
  const $ = _c(12);
  const {
    limit,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  let t3;
  if ($[0] !== limit) {
    t2 = [COMMUNITY_CURATIONS_DROPS_QUERY_KEY, {
      limit
    }];
    t3 = t4 => {
      const {
        pageParam
      } = t4;
      return fetchCommunityCurationsDrops({
        limit,
        page: pageParam
      });
    };
    $[0] = limit;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== enabled || $[4] !== t2 || $[5] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      enabled,
      initialPageParam: 1,
      getNextPageParam: _temp,
      staleTime: 60000
    };
    $[3] = enabled;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const query = useInfiniteQuery(t4);
  query.data?.pages;
  const t5 = query.data?.pages;
  let t6;
  if ($[7] !== t5) {
    t6 = getUniqueDrops(t5);
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const allDrops = t6;
  let t7;
  if ($[9] !== allDrops || $[10] !== query) {
    t7 = {
      ...query,
      allDrops,
      drops: allDrops
    };
    $[9] = allDrops;
    $[10] = query;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
}
function _temp(lastPage) {
  return lastPage.next ? lastPage.page + 1 : undefined;
}